/**
 * Chat sidebar component for desktop
 * Resizable sidebar with WebSocket chat functionality
 */

import { useQueryClient } from "@tanstack/react-query";
import { MessageSquare, PanelRightClose, Trash2 } from "lucide-react";
import { type MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useChatWebSocket } from "@/hooks/useChatWebSocket";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";

interface Props {
  width: number;
  onWidthChange: (width: number) => void;
  minWidth: number;
  maxWidth: number;
  onClose: () => void;
}

export function ChatSidebar({ width, onWidthChange, minWidth, maxWidth, onClose }: Props) {
  const { apiKey } = useAuth();
  const queryClient = useQueryClient();

  // Refetch queries when AI agent completes (may have created/modified data)
  const handleChatComplete = useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ["invoices"], type: "all" }),
      queryClient.refetchQueries({ queryKey: ["registries"], type: "all" }),
      queryClient.refetchQueries({ queryKey: ["invoiceCount"], type: "all" }),
    ]);
  }, [queryClient]);

  const { messages, sendMessage, clearHistory, isConnected, isStreaming } = useChatWebSocket({
    apiKey: apiKey || "",
    onComplete: handleChatComplete,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages.length triggers scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle resize drag
  const handleResizeStart = (e: ReactMouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      const delta = startX - e.clientX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
      onWidthChange(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <aside
      className="relative flex h-full flex-col border-l bg-white dark:border-gray-700 dark:bg-gray-900"
      style={{ width }}
      data-testid="chat-sidebar"
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 h-full w-1 cursor-ew-resize hover:bg-blue-500"
        onMouseDown={handleResizeStart}
        data-testid="chat-resize-handle"
      />

      {/* Header */}
      <div className="flex items-center justify-between border-b p-4 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">AI Assistant</h2>
          {!isConnected && <span className="text-xs text-red-500">Disconnected</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={clearHistory}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title="Clear history"
            data-testid="chat-clear-button"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title="Close sidebar"
            aria-label="Close sidebar"
            data-testid="chat-close-button"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center text-gray-500">
            <MessageSquare className="mb-4 h-12 w-12" />
            <p className="mb-2 font-medium">How can I help you?</p>
            <p className="text-sm">Ask me about invoices, registries, or sequences.</p>
            <div className="mt-4 space-y-2 text-sm">
              <button
                type="button"
                onClick={() => sendMessage("Show unpaid invoices")}
                className="block w-full rounded-lg border p-2 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                Show unpaid invoices
              </button>
              <button
                type="button"
                onClick={() => sendMessage("List all registries")}
                className="block w-full rounded-lg border p-2 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                List all registries
              </button>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <ChatMessage key={msg.id} role={msg.role} content={msg.content} isStreaming={msg.isStreaming} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <ChatInput
        onSend={sendMessage}
        disabled={!isConnected || isStreaming}
        placeholder={isStreaming ? "AI is responding..." : "Type a message..."}
      />
    </aside>
  );
}
